import React, { memo, useMemo, useState, useCallback, ChangeEvent, useEffect, forwardRef } from 'react';
import { Dropdown, Checkbox, Input } from 'antd';
import classnames from 'classnames';
import { FixedSizeList } from 'react-window';
import SelectAll from './select-all';

import './multiSelectDropdown.scss';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

const FILTERS_SEARCH_THRESHOLD = 10;
const MULTISELECT_FILTER_ITEM_HEIGHT = 35; // in px
const MULTISELECT_FILTER_ITEMS_IN_VIEW = 10.5; // count
const MULTISELECT_FILTER_CONFIG = {
	itemSize: MULTISELECT_FILTER_ITEM_HEIGHT,
	maxHeight: MULTISELECT_FILTER_ITEM_HEIGHT * MULTISELECT_FILTER_ITEMS_IN_VIEW
};

export interface Option<T = number> {
	id: T;
	name: string;
}

export interface MultiSelectTranslations {
	unknownItemText: string;
	searchItemPlaceholder: string;
	multiselectPlaceholder: string;
	oneItemSelected: string;
	multipleItemsSelected: string;
	allItemsSelected: string;
	clearSelectionText: string;
	selectAllText: string;
}

interface GetLabelProps<T> {
	value: T[];
	options: Option<T>[];
	translations: MultiSelectTranslations;
}

export interface MultiselectProps<T = number> extends GetLabelProps<T> {
	onChange: (value: T[]) => void;
	maxItems?: number;
	dataTestId?: string;
	label?: string;
}

function ColumnsMenuVirtualized<T>({ value, onChange, options, translations, maxItems }: MultiselectProps<T>) {
	const [searchQuery, setSearchQuery] = useState<string>('');
	const valuesSet = useMemo(() => new Set(value), [value]);
	const { itemSize, maxHeight } = MULTISELECT_FILTER_CONFIG;

	const searchEnabled = options.length > FILTERS_SEARCH_THRESHOLD;
	const allChecked = (maxItems && value.length === maxItems) || options.every(item => valuesSet.has(item.id));

	const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value), []);
	const handleCheckAll = useCallback(() => {
		if (allChecked) {
			onChange([]);
			return;
		}

		if (maxItems) {
			onChange(options.slice(0, maxItems).map(({ id }) => id));
		} else {
			onChange(options.map(({ id }) => id));
		}
	}, [onChange, options, allChecked, maxItems]);

	const filteredOptions = useMemo(() => {
		const normalizedSearchQuery = searchQuery.trim().toLowerCase();
		return searchEnabled && normalizedSearchQuery
			? options.filter(({ name }) => name?.toLowerCase().includes(normalizedSearchQuery) ?? false)
			: options;
	}, [options, searchQuery, searchEnabled]);

	useEffect(() => setSearchQuery(''), [options]);

	const OuterElementType = useMemo(
		() =>
			forwardRef((props, ref: React.LegacyRef<HTMLUListElement>) => (
				<ul ref={ref} {...props} className="ant-menu filters-menu" role="menu" />
			)),
		[]
	);

	const RenderRow = useCallback(
		({ index, style }) => {
			const { id, name } = filteredOptions[index];
			const checked = valuesSet.has(id);
			const itemName = name || translations.unknownItemText;
			const disabled = maxItems && value.length === maxItems && !value.includes(id);
			const setChecked = () => {
				if (disabled) {
					return;
				}
				onChange(checked ? value.filter(v => v !== id) : value.concat(id));
			};

			return (
				<li
					key={String(id)}
					onClick={setChecked}
					className={classnames('ant-menu-item', { disabled })}
					role="menuitem"
					style={style}
					title={itemName}
				>
					<Checkbox checked={checked} disabled={disabled} />
					{itemName}
				</li>
			);
		},
		[filteredOptions, valuesSet, onChange, value]
	);

	return (
		<div className="filter-overlay-wrapper">
			{searchEnabled && (
				<Input
					className="search-input"
					suffix={<BringgIcon iconName={BringgFontIcons.Search} />}
					placeholder={translations.searchItemPlaceholder}
					value={searchQuery}
					onChange={handleSearchChange}
				/>
			)}
			<SelectAll
				isAllSelected={allChecked}
				handleClick={handleCheckAll}
				clearSelectionText={translations.clearSelectionText}
				selectAllText={translations.selectAllText + (maxItems ? ' (' + maxItems + ')' : '')}
			/>
			<FixedSizeList
				itemSize={itemSize}
				height={Math.min(maxHeight, filteredOptions.length * itemSize)}
				itemCount={filteredOptions.length}
				width="100%"
				outerElementType={OuterElementType}
			>
				{RenderRow}
			</FixedSizeList>
		</div>
	);
}

const MemoColumnsMenuVirtualized = memo(ColumnsMenuVirtualized) as typeof ColumnsMenuVirtualized;

function getFilterValueLabel<T>({ value, options, translations }: GetLabelProps<T>) {
	const { multiselectPlaceholder, oneItemSelected, multipleItemsSelected, allItemsSelected } = translations;

	if (!value.length) {
		return <span className="multiselect-placeholder">{multiselectPlaceholder}</span>;
	}

	let label = '';

	if (value.length === options.length) {
		label = allItemsSelected;
	} else {
		const { length } = value;
		const text = length === 1 ? oneItemSelected : multipleItemsSelected;
		label = `${length} ${text}`;
	}

	return <span className="multiselect-label">{label}</span>;
}

type MultiselectTrigger = 'click' | 'hover' | 'contextMenu';

function FilterMultiselect<T = number>(props: MultiselectProps<T>) {
	const { options, value, translations, dataTestId, label } = props;
	const [menuVisible, setMenuVisible] = useState<boolean>(false);

	const className = classnames('multiselect-wrapper', {
		active: menuVisible
	});
	const trigger: MultiselectTrigger[] = useMemo(() => ['click'], []);
	const filterValueLabel = useMemo(() => getFilterValueLabel({ options, value, translations }), [
		options,
		value,
		translations
	]);

	// TODO: use renderValue prop to render multiselect-label
	return (
		<div className="filter-item" data-test-id={dataTestId}>
			{label && <label className="filters-search-label">{label}:</label>}
			<Dropdown
				overlay={<MemoColumnsMenuVirtualized<T> {...props} />}
				trigger={trigger}
				onOpenChange={setMenuVisible}
			>
				<div className={className}>
					<div className="multiselect-text-wrapper">{filterValueLabel}</div>
					<BringgIcon iconName={menuVisible ? BringgFontIcons.ChevronUp : BringgFontIcons.Chevron} />
				</div>
			</Dropdown>
		</div>
	);
}

export default memo(FilterMultiselect) as typeof FilterMultiselect;
